import React, { useState, useEffect, useRef } from 'react';
import { Phone, Menu, X, ChevronDown, Mail, ArrowLeft } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { companyInfo } from '../mockData';

const navItems = [
  {
    label: 'Our Mission',
    sectionId: null,
    route: '/mission',
    subItems: []
  },
  {
    label: 'Residential',
    sectionId: 'services',
    subItems: [
      { label: 'Home Electrical Services', sectionId: 'services' },
      { label: 'Smart Home & Automation', sectionId: 'services' },
      { label: 'Panel Upgrades', sectionId: 'services' },
      { label: 'EV Charger Installations', sectionId: 'services' },
      { label: 'Silver Label Inspections', sectionId: 'services' }
    ]
  },
  {
    label: 'Commercial',
    sectionId: 'services',
    subItems: [
      { label: 'Commercial Electrical', sectionId: 'services' },
      { label: 'Lighting & Power Upgrades', sectionId: 'services' },
      { label: 'Service & Maintenance', sectionId: 'services' },
      { label: 'Showcase & Parking Lighting', sectionId: 'services' }
    ]
  },
  {
    label: 'Industrial',
    sectionId: 'services',
    subItems: [
      { label: 'Industrial Electrical', sectionId: 'services' },
      { label: 'Automation & Controls', sectionId: 'services' },
      { label: 'HVAC Control Systems', sectionId: 'services' },
      { label: 'Network Infrastructure', sectionId: 'services' },
      { label: 'Water & Wastewater Systems', sectionId: 'services' }
    ]
  },
  {
    label: 'Renewable Energy',
    sectionId: 'renewable',
    subItems: [
      { label: 'Solar Power Systems', sectionId: 'renewable' },
      { label: 'Mini-Hydro Systems', sectionId: 'renewable' },
      { label: 'Micro-Wind Turbines', sectionId: 'renewable' },
      { label: 'Battery Storage', sectionId: 'renewable' }
    ]
  },
  {
    label: 'Off-Grid',
    sectionId: 'renewable',
    subItems: [
      { label: 'Off-Grid Solutions', sectionId: 'renewable' },
      { label: 'Homestead Power', sectionId: 'renewable' },
      { label: 'Remote Site Power', sectionId: 'renewable' },
      { label: 'Backup Generators', sectionId: 'renewable' }
    ]
  },
  {
    label: 'Contact',
    sectionId: null,
    route: '/contact',
    subItems: []
  }
];

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const dropdownTimeout = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    if (!sectionId) {
      if (isHome) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        navigate('/');
      }
    } else {
      if (!isHome) {
        navigate('/', { state: { scrollTo: sectionId } });
      } else {
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setMobileMenuOpen(false);
    setActiveDropdown(null);
    setMobileExpanded(null);
  };

  const handleMouseEnter = (index) => {
    if (dropdownTimeout.current) clearTimeout(dropdownTimeout.current);
    setActiveDropdown(index);
  };

  const handleMouseLeave = () => {
    dropdownTimeout.current = setTimeout(() => setActiveDropdown(null), 150);
  };

  const toggleMobileExpand = (index) => {
    setMobileExpanded(mobileExpanded === index ? null : index);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-black shadow-lg shadow-black/30'
          : 'bg-black'
      } border-b border-gold/15`}
    >
      <div className="container mx-auto px-4 lg:px-6 xl:px-8">
        <div className="flex items-center justify-between h-[72px]">

          {/* Logo */}
          <button
            data-testid="header-logo"
            onClick={() => scrollToSection(null)}
            className="flex items-center gap-2 group shrink-0"
          >
            <div className="flex flex-col leading-tight">
              <span className="text-lg lg:text-xl font-bold text-gold tracking-tight group-hover:text-white transition-colors">
                Ol' McToodles
              </span>
              <span className="text-sm font-semibold text-gold tracking-wide">
                (OME)
              </span>
            </div>
          </button>

          {/* Desktop Navigation */}
          <nav
            data-testid="desktop-nav"
            className="hidden lg:flex items-center gap-1"
          >
            {navItems.map((item, index) => (
              <div
                key={index}
                className="relative"
                onMouseEnter={() => handleMouseEnter(index)}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  data-testid={`nav-item-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => item.route ? navigate(item.route) : scrollToSection(item.sectionId)}
                  className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    activeDropdown === index
                      ? 'text-gold bg-gold/10'
                      : 'text-white/85 hover:text-gold hover:bg-white/5'
                  }`}
                >
                  <span>{item.label}</span>
                  {item.subItems.length > 0 && (
                    <ChevronDown
                      className={`w-3.5 h-3.5 transition-transform duration-200 ${
                        activeDropdown === index ? 'rotate-180 text-gold' : 'text-white/50'
                      }`}
                    />
                  )}
                </button>

                {/* Dropdown */}
                {item.subItems.length > 0 && activeDropdown === index && (
                  <div
                    data-testid={`dropdown-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className="absolute top-full left-0 mt-1 w-56 bg-black/98 border border-gold/20 rounded-xl shadow-2xl shadow-black/50 py-2 animate-in fade-in slide-in-from-top-2 duration-200"
                  >
                    {item.subItems.map((sub, subIndex) => (
                      <button
                        key={subIndex}
                        data-testid={`dropdown-item-${sub.label.toLowerCase().replace(/\s+/g, '-')}`}
                        onClick={() => scrollToSection(sub.sectionId)}
                        className="w-full text-left px-4 py-2.5 text-sm text-white/80 hover:text-gold hover:bg-gold/8 transition-colors flex items-center gap-2"
                      >
                        <span className="w-1 h-1 rounded-full bg-gold/40 shrink-0" />
                        {sub.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Phone CTA - Desktop */}
          <div className="hidden lg:flex items-center gap-3 shrink-0">
            <a
              data-testid="header-phone-link"
              href={`tel:${companyInfo.phone}`}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gold/30 text-gold hover:bg-gold hover:text-black font-semibold text-sm transition-all duration-200"
            >
              <Phone className="w-4 h-4" />
              <span>{companyInfo.phone}</span>
            </a>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-white p-2 hover:bg-white/10 rounded-lg transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-black/98 border-t border-gold/15 max-h-[calc(100vh-72px)] overflow-y-auto"
        >
          <nav className="container mx-auto px-4 py-4 space-y-1">
            {navItems.map((item, index) => (
              <div key={index}>
                <button
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => {
                    if (item.route) {
                      navigate(item.route);
                      setMobileMenuOpen(false);
                    } else if (item.subItems.length > 0) {
                      toggleMobileExpand(index);
                    } else {
                      scrollToSection(item.sectionId);
                    }
                  }}
                  className="w-full flex items-center justify-between py-3 px-3 rounded-lg text-white hover:text-gold hover:bg-gold/5 transition-colors"
                >
                  <span className="font-medium">{item.label}</span>
                  {item.subItems.length > 0 && (
                    <ChevronDown
                      className={`w-4 h-4 text-gold/60 transition-transform duration-200 ${
                        mobileExpanded === index ? 'rotate-180' : ''
                      }`}
                    />
                  )}
                </button>

                {/* Mobile Sub-items */}
                {item.subItems.length > 0 && mobileExpanded === index && (
                  <div className="ml-3 pl-3 border-l-2 border-gold/20 space-y-1 pb-2 animate-in slide-in-from-top-1 duration-150">
                    {/* View all link */}
                    <button
                      onClick={() => scrollToSection(item.sectionId)}
                      className="w-full text-left py-2 px-3 text-sm text-gold/80 hover:text-gold font-medium rounded-md hover:bg-gold/5 transition-colors"
                    >
                      View All {item.label}
                    </button>
                    {item.subItems.map((sub, subIndex) => (
                      <button
                        key={subIndex}
                        onClick={() => scrollToSection(sub.sectionId)}
                        className="w-full text-left py-2 px-3 text-sm text-white/70 hover:text-gold rounded-md hover:bg-gold/5 transition-colors"
                      >
                        {sub.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Mobile Contact Links */}
            <div className="pt-3 mt-3 border-t border-gold/15 space-y-2">
              <a
                href={`tel:${companyInfo.phone}`}
                className="flex items-center gap-3 py-3 px-3 rounded-lg text-gold font-semibold hover:bg-gold/5 transition-colors"
              >
                <Phone className="w-5 h-5" />
                <span>{companyInfo.phone}</span>
              </a>
              <a
                href={`mailto:${companyInfo.email}`}
                className="flex items-center gap-3 py-3 px-3 rounded-lg text-gold/80 hover:text-gold hover:bg-gold/5 transition-colors"
              >
                <Mail className="w-5 h-5" />
                <span>{companyInfo.email}</span>
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
